import * as Yup from "yup";
import { PregStatuses } from "constants/draftingAttributes";
import { dateTimeStringToDateTime } from "lib/timeFormats";
import { diffHornHdAllocations } from "lib/validation/allocation";

export interface ErrorDict {
  [key: string]: string;
}

export function doValidate(
  schema: Yup.BaseSchema,
  saleLot: SaleLot,
): ErrorDict {
  try {
    // Abort early is a hard one.  We want to get ALL the errors, so we need to set it to false when using validatedSync.
    // But, it means the validators are a bit sensitive - the .test calls may not have context, etc.
    schema.validateSync(saleLot, { abortEarly: false });
  } catch (e) {
    return e.inner.reduce((acc: ErrorDict, error: Yup.ValidationError) => {
      acc[error.path] = error.message;
      return acc;
    }, {} as ErrorDict);
  }
  return null;
}

/* Simple required fields */

export const vendorRequiredValidation = {
  consignment: Yup.object({
    vendor_id: Yup.string()
      .typeError("Vendor is required.")
      .required("Vendor is required."),
    vendor_property_id: Yup.string()
      .typeError("Vendor Property is required.")
      .required("Vendor Property is required."),
  }).typeError("Vendor Details incomplete."),
};

export const sheepFieldsRequired = {
  age_id: Yup.number().typeError("Age is required").required("Age is required"),

  breed_id: Yup.number()
    .typeError("Breed is required")
    .required("Breed is required"),
  sex_id: Yup.number().typeError("Sex is required").required("Sex is required"),
};

export const sheepDraftingAttributesRequired = {
  dropStart: Yup.string()
    .typeError("Drop Start is required.")
    .required("Drop start required."),
  dropEnd: Yup.string()
    .typeError("Drop End is required.")
    .required("Drop end required."),
};

/* Tests whether a preg status has all (or sufficient) data related to it where needed. */
export const pregStatusValidation = {
  pregStatus: Yup.string()
    .typeError("Preg Status information incomplete.")
    .required("Preg Status information incomplete.")
    .test(
      "pregStatusTest",
      "SIL Must be provided when preg status is SIL.",
      (value, context) => {
        if (!context) {
          return false;
        }
        if (value !== PregStatuses.SIL) {
          return true;
        }
        return Boolean(context.parent.sil);
      },
    )
    .test(
      "validPregStatus",
      "Missing joined and despastured information.",
      (value, context): boolean => {
        if (!context) {
          return true;
        }
        if (
          [
            PregStatuses.SIL,
            PregStatuses.STATION_MATED,
            PregStatuses.NOT_STATION_MATED,
          ].includes(value)
        ) {
          // We don't need this data, so no further validation here.
          return true;
        }
        // otherwise we need joined start, joined end, depasturedId, and make sure the start < end.
        const { joinedStart, joinedEnd, despasturedId } = context.parent;
        if (!joinedStart || !joinedEnd || !despasturedId) {
          return false;
        }
        const joinedStartDateTime = dateTimeStringToDateTime(joinedStart);
        const joinedEndDateTime = dateTimeStringToDateTime(joinedEnd);
        const now = new Date();
        return (
          joinedStartDateTime < now &&
          joinedEndDateTime < now &&
          joinedStartDateTime <= joinedEnd
        );
      },
    ),
};

export const hornValidation = {
  hornedComplete: Yup.number().test(
    "hornedTest",
    "Horned incomplete.",
    (_ignored, context: any) => {
      if (!context) {
        return false;
      }
      return diffHornHdAllocations(context.from[1].value) === 0;
    },
  ),
};

export const cattleAgeRequiredValidation = {
  minAge: Yup.number()
    .typeError("Min Age is required")
    .required("Min Age is required"),
  maxAge: Yup.number()
    .typeError("Max Age is required")
    .required("Max Age is required"),
  ageRangeTimeUnit: Yup.string()
    .typeError("Age Range Unit is required")
    .required("Age Range Unit is required"),
};
